import {
  COMPLIMENTARY_NIGHT_PROMOTION_TITLE,
  GENERIC_ERROR_SUBTITLE,
  GENERIC_SEARCH_ERROR_TITLE,
  GO_BACK_BUTTON,
  localCache,
  PATH_PREMIUM_OFFERS_SHOP,
  SEARCH_AGAIN_BUTTON,
} from "@capone/common";
import { LUXURY_COMPLIMENTARY_NIGHT_PROMOTION } from "@capone/experiments";
import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";
import dayjs from "dayjs";
import {
  B2BLoadingPopup,
  ensureExhaustive,
  GENERIC_HOTEL_PROMOTION_OFFER_IMAGE,
  GenericInfoPopup,
  getAgentErrorSubtitle,
  getAgentErrorTitle,
  Icon,
  IconName,
  PremiumCollectionLodgingPromotionSearch,
  PremiumCollectionPromotionAvailabilityCard,
  PromotionAvailability,
  useDeviceTypes,
  validateRestProps,
} from "halifax";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactList from "react-list";
import { RouterProps, useHistory } from "react-router";
import {
  buildEncodedPersistentLodgingTrackingProperties,
  CASH,
  DatelessSearchSource,
  FILTER_APPLIED_PREMIUM_OFFERS,
  FilterAppliedPremiumOffersProperties,
  GuestsSelection,
  LodgingCollectionEnum,
  MILES,
  MODAL_ALERT,
  MODAL_ALERT_CHOICE,
  ModalButtonType,
  ModalCategoryType,
  ModalScreens,
  PATH_PREMIER_COLLECTION_SHOP_DETAILS,
  PlatformEnum,
  PremiumOffersFilterType,
  RewardsAccount,
  SELECTED_HOTEL_FROM_PREMIUM_OFFER_LIST,
  SelectedHotelOfferState,
  TrackEventRequest,
  transformToShopDetailsStringifiedQuery,
} from "redmond";
import { v4 as uuidv4 } from "uuid";
import { trackEvent } from "../../api/v0/analytics/trackEvent";
import {
  AVAILABLE,
  CACHE_HOTEL_TOKEN,
  FTC_PRICING_UPDATE,
  FTC_PRICING_UPDATE_VARIANTS,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  useExperiments,
} from "../../context/experiments";
import { PATH_HOME, PATH_SHOP } from "../../utils/paths";
import { transformToStringifiedQuery } from "../shop/utils/queryStringHelpers";
import { PremierCollectionAvailabilityConnectorProps } from "./container";
import { SortOption } from "./reducer";
import {
  filterLodgingsForOffers,
  getRewardsPropsForLodging,
  getSortOptions,
  OFFERS_STATES,
  OffersState,
  ProcessedLodgingForOffers,
  sortLodgingsForOffers,
} from "./reducer/selectors";
import styles from "./styles.module.scss";
import {
  BANNER_SUBTITLE,
  LOADING_POPUP_SUBTITLE_VENTURE,
  LOADING_POPUP_SUBTITLE_VENTURE_X,
  LOADING_POPUP_TITLE,
  VALID_THROUGH_MOBILE,
  VIEW_TERMS_AND_DETAILS,
} from "./textConstants";
import { useGridColumnCount } from "./utils/useGridColumnCount";
import { useOffersSearchParams } from "./utils/useOffersSearchParams";

const GRID_MIN_CARD_WIDTH = 325;
const GRID_MAX_CARD_WIDTH = 360;
const GRID_GAP_DESKTOP = 40;
const GRID_GAP_MOBILE = 16;
const GRID_PADDING_DESKTOP = 118;
const GRID_PADDING_MOBILE = 16;
// Slightly overestimating the card height to avoid list scrolling issues when re-sizing
const ESTIMATED_CARD_HEIGHT = 472;

function renderGridRow(rows: JSX.Element[][]) {
  return (rowIdx: number) => (
    <Box key={rowIdx} className={styles.gridRow}>
      {rows[rowIdx].map((card) => card)}
    </Box>
  );
}

const PremiumCollectionOffersSuccess = ({
  offersState,
  rewardsAccount,
  selectedRewardsAccountIndex,
  setSelectedLodgingIndex,
  cacheHotelTokenEnabled,
  selectedFilterCategory,
  selectedSortOption,
}: {
  offersState: Extract<OffersState, { state: typeof OFFERS_STATES.SUCCESS }>;
  rewardsAccount: RewardsAccount | undefined;
  selectedRewardsAccountIndex: number;
  setSelectedLodgingIndex: (index: number) => void;
  cacheHotelTokenEnabled: boolean;
  selectedFilterCategory: PremiumOffersFilterType;
  selectedSortOption: SortOption;
}) => {
  const history = useHistory();
  const { matchesMobile } = useDeviceTypes();

  const handleLodgingClick = (lodging: ProcessedLodgingForOffers) => {
    setSelectedLodgingIndex(lodging.core.index);

    if (lodging.shop.available) {
      const shopParams = transformToStringifiedQuery({
        lodgingId: lodging.core.lodgingId,
        lodgingSelection: "",
        fromDate: dayjs(
          offersState.shopNavigationParams.stayDates.from
        ).toDate(),
        untilDate: dayjs(
          offersState.shopNavigationParams.stayDates.until
        ).toDate(),
        adultsCount: offersState.shopNavigationParams.guests.adults,
        childrenCount: offersState.shopNavigationParams.guests.children.length,
        selectedAccountIndex: selectedRewardsAccountIndex,
        selectedLodgingIndex: lodging.core.index,
        shopToken: cacheHotelTokenEnabled
          ? lodging.shop.opaqueShopRequest
          : localCache.set(uuidv4(), lodging.shop.opaqueShopRequest || "").key,
        isFromPremiumOffers: true,
        encodedPersistentLodgingTrackingProperties:
          buildEncodedPersistentLodgingTrackingProperties(
            lodging.core.originalLodging
          ),
      });
      if (matchesMobile)
        history.push(`${PATH_SHOP}${shopParams}`, {
          fromPage: history.location.pathname,
        });
      else window.open(`${PATH_SHOP}${shopParams}`, "_blank");
    }
  };

  const handleDatelessLodgingClick = (lodging: ProcessedLodgingForOffers) => {
    const shopParams = transformToShopDetailsStringifiedQuery({
      lodgingId: lodging.core.lodgingId,
      source: DatelessSearchSource.OffersSearchPage,
      unavailable: true,
    });
    if (matchesMobile) {
      history.push({
        pathname: PATH_PREMIER_COLLECTION_SHOP_DETAILS,
        search: shopParams,
      });
    } else {
      window.open(
        `${PATH_PREMIER_COLLECTION_SHOP_DETAILS}${shopParams}`,
        "_blank"
      );
    }
  };

  const getSelectedHotelFromPremiumOfferListTrackingEvent = (
    lodging: ProcessedLodgingForOffers
  ): TrackEventRequest => {
    const { state } = lodging;
    const isAvailable = state !== PromotionAvailability.HotelUnavailable;

    let offerState: SelectedHotelOfferState;
    switch (state) {
      case PromotionAvailability.HotelUnavailable:
        offerState = SelectedHotelOfferState.UNAVAILABLE;
        break;
      case PromotionAvailability.PromotionUnavailable:
        offerState = SelectedHotelOfferState.LOCKED;
        break;
      case PromotionAvailability.PromotionAvailable:
        offerState = SelectedHotelOfferState.UNLOCKED;
        break;
      default:
        ensureExhaustive(state);
    }

    return {
      eventName: SELECTED_HOTEL_FROM_PREMIUM_OFFER_LIST,
      properties: {
        lodging_id: lodging.core.lodgingId,
        collection: lodging.core.lodgingCollection,
        offer_state: offerState,
        is_available: isAvailable,
      },
    };
  };

  const filteredLodgings = useMemo(
    () => filterLodgingsForOffers(offersState.lodgings, selectedFilterCategory),
    [offersState.lodgings, selectedFilterCategory]
  );

  const sortedAndFilteredLodgings = useMemo(
    () => sortLodgingsForOffers(filteredLodgings, selectedSortOption),
    [filteredLodgings, selectedSortOption]
  );

  const onClickHandlers = useMemo(() => {
    const handlers = new Map<string, () => void>();
    sortedAndFilteredLodgings.forEach((lodging) => {
      const trackingEvent =
        getSelectedHotelFromPremiumOfferListTrackingEvent(lodging);

      if (lodging.state === PromotionAvailability.HotelUnavailable) {
        handlers.set(lodging.core.lodgingId, () => {
          trackEvent(trackingEvent);
          setSelectedLodgingIndex(lodging.core.index);
          handleDatelessLodgingClick(lodging);
        });
      } else {
        handlers.set(lodging.core.lodgingId, () => {
          trackEvent(trackingEvent);
          handleLodgingClick(lodging);
        });
      }
    });
    return handlers;
  }, [
    sortedAndFilteredLodgings,
    getSelectedHotelFromPremiumOfferListTrackingEvent,
    setSelectedLodgingIndex,
    handleDatelessLodgingClick,
    handleLodgingClick,
    trackEvent,
  ]);

  const rewards = useMemo(
    () =>
      getRewardsPropsForLodging(
        (
          rewardsAccount?.rewardsBalance?.currencyDescription ??
          rewardsAccount?.rewardsBalance?.currency
        )?.includes("cash")
          ? CASH
          : MILES,
        rewardsAccount
      ),
    [rewardsAccount]
  );

  const MemoizedCard = React.memo(PremiumCollectionPromotionAvailabilityCard);
  const cardCacheRef = useRef<Map<string, JSX.Element>>(new Map());

  const cards = sortedAndFilteredLodgings.map((lodging) => {
    const key = lodging.core.lodgingId;
    if (!cardCacheRef.current.has(key)) {
      const commonProps = {
        lodgingCollection: lodging.core.lodgingCollection,
        images: lodging.core.images,
        lodgingName: lodging.core.lodgingName,
        lodgingRating: lodging.core.lodgingRating,
        lodgingCity: lodging.core.lodgingCity,
        nights: lodging.promotion?.nights,
        promotionAvailability: lodging.state,
        promotionStartDate: lodging.promotion?.promotionStartDate?.toDate(),
        promotionEndDate: lodging.promotion?.promotionEndDate?.toDate(),
        rewards,
      };

      let card: JSX.Element;

      switch (lodging.state) {
        case PromotionAvailability.HotelUnavailable:
          card = (
            <MemoizedCard
              key={key}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...commonProps}
              onClick={onClickHandlers.get(key)!}
            />
          );
          break;

        case PromotionAvailability.PromotionUnavailable:
          card = (
            <MemoizedCard
              key={key}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...commonProps}
              pricePerNight={lodging.prices?.pricePerNight}
              priceTotal={lodging.prices?.priceTotal}
              totalPriceInMiles={lodging.rewards?.totalPriceInMiles}
              propertyFees={lodging.prices?.propertyFees}
              onClick={onClickHandlers.get(key)!}
            />
          );
          break;

        case PromotionAvailability.PromotionAvailable:
          card = (
            <MemoizedCard
              key={key}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...commonProps}
              strikethroughPricePerNight={
                lodging.prices.strikethroughPricePerNight
              }
              pricePerNight={lodging.prices.pricePerNight}
              strikethroughPriceTotal={lodging.prices.strikethroughPriceTotal}
              priceTotal={lodging.prices.priceTotal}
              totalPriceInMiles={lodging.rewards?.totalPriceInMiles}
              onClick={onClickHandlers.get(key)!}
            />
          );
          break;

        default:
          return ensureExhaustive(lodging);
      }

      cardCacheRef.current.set(key, card);
    }

    return cardCacheRef.current.get(key)!;
  });

  const gridRef = useRef<HTMLDivElement>(null);
  const gridGap = matchesMobile ? GRID_GAP_MOBILE : GRID_GAP_DESKTOP;
  const gridPadding = matchesMobile
    ? GRID_PADDING_MOBILE
    : GRID_PADDING_DESKTOP;
  const { columns } = useGridColumnCount(
    gridRef,
    GRID_MIN_CARD_WIDTH,
    GRID_MAX_CARD_WIDTH,
    gridGap,
    { left: gridPadding, right: gridPadding }
  );

  const rows = useMemo(() => {
    const result: JSX.Element[][] = [];
    for (let i = 0; i < cards.length; i += columns) {
      const row = cards.slice(i, i + columns);
      // Pad with empty boxes if last row is short
      while (row.length < columns) {
        row.push(
          <Box key={`spacer-${i + row.length}`} className={styles.spacer} />
        );
      }
      result.push(row);
    }
    return result;
  }, [cards, columns]);

  const rowRenderer = useCallback(renderGridRow(rows), [rows, gridGap]);

  return (
    <div
      ref={gridRef}
      className={clsx(styles.lodgingsResult, {
        [styles.mobile]: matchesMobile,
      })}
    >
      <ReactList
        itemRenderer={rowRenderer}
        length={rows.length}
        type="variable"
        itemSizeEstimator={() => ESTIMATED_CARD_HEIGHT}
      />
    </div>
  );
};

export const PremiumCollectionOffers = ({
  offersState,
  selectedSortOption,
  fetchPremiumOffersShop,
  rewardsAccount,
  selectedAccountIndex,
  setSelectedLodgingIndex,
  setOffersSort,
  setIsUserTriggeredSearch,
  isVentureX,
  selectedFilterCategory,
  setSelectedFilterCategory,
  ftcPricingUpdateVariant,
  setFTCPricingUpdateVariant,
  ...rest
}: PremierCollectionAvailabilityConnectorProps & RouterProps) => {
  validateRestProps<PremierCollectionAvailabilityConnectorProps>(rest);
  const history = useHistory();
  const expState = useExperiments();
  const searchParams = useOffersSearchParams();
  const sortOptions = getSortOptions();
  const { matchesMobile } = useDeviceTypes();

  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);

  const complimentaryNightPromotionVariant = getExperimentVariant(
    expState.experiments,
    LUXURY_COMPLIMENTARY_NIGHT_PROMOTION
  );

  useEffect(() => {
    if (complimentaryNightPromotionVariant !== AVAILABLE) {
      history.replace(PATH_HOME);
    }
  }, [complimentaryNightPromotionVariant, history]);

  if (complimentaryNightPromotionVariant !== AVAILABLE) {
    return null;
  }

  useEffect(() => {
    fetchPremiumOffersShop(history, searchParams);
  }, [fetchPremiumOffersShop, history, searchParams]);

  const cacheHotelTokenVariant = getExperimentVariant(
    expState.experiments,
    CACHE_HOTEL_TOKEN
  );

  const cacheHotelTokenEnabled = React.useMemo(
    () => cacheHotelTokenVariant === AVAILABLE,
    [cacheHotelTokenVariant]
  );

  const experimentVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    FTC_PRICING_UPDATE,
    FTC_PRICING_UPDATE_VARIANTS
  );
  React.useEffect(() => {
    if (experimentVariant !== ftcPricingUpdateVariant) {
      setFTCPricingUpdateVariant(experimentVariant);
    }
  }, [experimentVariant, ftcPricingUpdateVariant, setFTCPricingUpdateVariant]);

  const getModalEventProperties = (error: Error) => ({
    type: "premium_offers_availability_error",
    primary_button: SEARCH_AGAIN_BUTTON,
    secondary_button: GO_BACK_BUTTON,
    screen: ModalScreens.PREMIUM_OFFERS_SEARCH,
    category: ModalCategoryType.TROUBLE,
    modal_subtitle: GENERIC_ERROR_SUBTITLE,
    modal_title: GENERIC_SEARCH_ERROR_TITLE,
    agent_subtitle: getAgentErrorSubtitle(error.message),
    agent_title: getAgentErrorTitle(error.message),
    funnel: "premier_collection",
    step: "availability",
  });

  React.useEffect(() => {
    if (offersState.state === OFFERS_STATES.ERROR) {
      setIsErrorDialogOpen(true);
      const modalEventProperties = getModalEventProperties(offersState.error);
      trackEvent({
        eventName: MODAL_ALERT,
        properties: { ...modalEventProperties },
      });
    }
  }, [offersState.state]);

  const renderLoadingPopup = () => {
    const subtitle = isVentureX
      ? LOADING_POPUP_SUBTITLE_VENTURE_X
      : LOADING_POPUP_SUBTITLE_VENTURE;
    return (
      <B2BLoadingPopup
        open
        message={LOADING_POPUP_TITLE}
        secondaryMessage={rewardsAccount ? subtitle : "..."}
        image={GENERIC_HOTEL_PROMOTION_OFFER_IMAGE}
        className={styles.loadingPopup}
        popupSize={matchesMobile ? "mobile" : "desktop"}
      />
    );
  };

  const renderErrorDialog = (error: Error) => {
    const modalEventProperties = getModalEventProperties(error);

    const buttons = [
      {
        buttonText: GO_BACK_BUTTON,
        buttonWrapperClassName: clsx("error-modal-button", "secondary"),
        onClick: () => {
          setIsErrorDialogOpen(false);
          trackEvent({
            eventName: MODAL_ALERT_CHOICE,
            properties: {
              ...modalEventProperties,
              button_choice: ModalButtonType.SECONDARY,
            },
          });

          const path = PATH_PREMIUM_OFFERS_SHOP;
          history.push(path);
        },
      },
      {
        buttonText: SEARCH_AGAIN_BUTTON,
        buttonWrapperClassName: clsx("error-modal-button", "primary"),
        onClick: () => {
          setIsErrorDialogOpen(false);
          trackEvent({
            eventName: MODAL_ALERT_CHOICE,
            properties: {
              ...modalEventProperties,
              button_choice: ModalButtonType.PRIMARY,
            },
          });
          fetchPremiumOffersShop(history, searchParams);
        },
      },
    ];

    return (
      <GenericInfoPopup
        agentSubtitle={getAgentErrorSubtitle(error.message)}
        agentTitle={getAgentErrorTitle(error.message)}
        buttons={buttons}
        className="hotel-availability-error-modal-root"
        image={<Icon className="error-icon" name={IconName.ErrorState} />}
        isAgent={false}
        isMobile={matchesMobile}
        open={isErrorDialogOpen}
        subtitle={GENERIC_ERROR_SUBTITLE}
        title={GENERIC_SEARCH_ERROR_TITLE}
      />
    );
  };

  const renderContent = () => {
    switch (offersState.state) {
      case OFFERS_STATES.LOADING:
        return renderLoadingPopup();
      case OFFERS_STATES.SUCCESS:
        return (
          <PremiumCollectionOffersSuccess
            offersState={offersState}
            rewardsAccount={rewardsAccount}
            selectedRewardsAccountIndex={selectedAccountIndex}
            setSelectedLodgingIndex={setSelectedLodgingIndex}
            cacheHotelTokenEnabled={cacheHotelTokenEnabled}
            selectedFilterCategory={selectedFilterCategory}
            selectedSortOption={selectedSortOption}
          />
        );
      case OFFERS_STATES.ERROR: {
        return renderErrorDialog(offersState.error);
      }
      default:
        return ensureExhaustive(offersState);
    }
  };

  return (
    <Box
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
    >
      <Box
        className={clsx(styles.header, {
          [styles.mobile]: matchesMobile,
        })}
      >
        <Box>
          <Typography variant="h1">
            {COMPLIMENTARY_NIGHT_PROMOTION_TITLE}
          </Typography>
          <Typography variant="h2">
            {BANNER_SUBTITLE(isVentureX)}{" "}
            {!matchesMobile && VIEW_TERMS_AND_DETAILS}
          </Typography>
          {matchesMobile && (
            <Typography variant="h3">
              {VALID_THROUGH_MOBILE} {VIEW_TERMS_AND_DETAILS}
            </Typography>
          )}
        </Box>
        <img
          src={GENERIC_HOTEL_PROMOTION_OFFER_IMAGE}
          alt={COMPLIMENTARY_NIGHT_PROMOTION_TITLE}
        />
      </Box>
      <PremiumCollectionLodgingPromotionSearch
        className={styles.search}
        onSearchChange={(
          startDate: Date,
          endDate: Date,
          travelersCounts: GuestsSelection
        ) => {
          setIsUserTriggeredSearch(true);
          fetchPremiumOffersShop(history, {
            stayDates: {
              from: dayjs(startDate).format("YYYY-MM-DD"),
              until: dayjs(endDate).format("YYYY-MM-DD"),
            },
            guests: {
              adults: travelersCounts.adults,
              children: travelersCounts.children,
            },
            rooms: {
              numberOfRooms: travelersCounts.rooms,
            },
            platform: PlatformEnum.Desktop,
            lodgingCollection: LodgingCollectionEnum.Premier,
          });
        }}
        startDate={dayjs(searchParams.stayDates.from).toDate()}
        endDate={dayjs(searchParams.stayDates.until).toDate()}
        months={[]}
        priceTags={[]}
        travelersCounts={{
          adults: searchParams.guests.adults,
          children: searchParams.guests.children,
          rooms: searchParams.rooms?.numberOfRooms ?? 1,
        }}
        showRoomCounts={false}
        filterCategories={
          offersState.state === OFFERS_STATES.SUCCESS
            ? offersState.filterCategories
            : [{ label: PremiumOffersFilterType.ALL }]
        }
        selectedFilterCategory={selectedFilterCategory}
        onSelectedFilterCategoryChange={(value: PremiumOffersFilterType) => {
          trackEvent({
            eventName: FILTER_APPLIED_PREMIUM_OFFERS,
            properties: {
              filter_type: value,
              sort_type: selectedSortOption,
            } as FilterAppliedPremiumOffersProperties,
          });
          setSelectedFilterCategory(value);
        }}
        sortOptions={sortOptions}
        sortValue={selectedSortOption}
        onSortChange={(value) => {
          setOffersSort(value as SortOption);
          trackEvent({
            eventName: FILTER_APPLIED_PREMIUM_OFFERS,
            properties: {
              filter_type: selectedFilterCategory,
              sort_type: value,
            } as FilterAppliedPremiumOffersProperties,
          });
        }}
      />
      {renderContent()}
    </Box>
  );
};
